<template>
  <section class="how-it-works" id="how-it-works">
    <div class="container">
      <h2 class="section-title fade-up fade-up-delay-1" v-html="titleHtml"></h2>
      <p class="section-desc fade-up fade-up-delay-2">{{ $t('hiw.desc') }}</p>

      <div class="hiw-grid">
        <div class="hiw-steps">
          <div
            v-for="(step, i) in $tm('hiw.steps')"
            :key="i"
            class="hiw-step"
            :class="{ active: currentStep === i }"
            @click="currentStep = i"
          >
            <div class="step-num">{{ step.num }}</div>
            <div class="step-content">
              <h3>{{ step.title }}</h3>
              <p>{{ step.desc }}</p>
            </div>
          </div>
        </div>

        <div class="hiw-visual fade-up fade-up-delay-2">

          <!-- Visual 0: Tasks board screenshot -->
          <div class="hiw-visual-card" :class="{ visible: currentStep === 0 }">
            <div class="hiw-card-header">
              <div style="display:flex;gap:5px;">
                <div class="app-preview-dot dot-red"></div>
                <div class="app-preview-dot dot-yellow"></div>
                <div class="app-preview-dot dot-green"></div>
              </div>
              <div class="hiw-card-title">{{ $t('hiw.visuals.boardTitle') }}</div>
            </div>
            <div class="hiw-card-body hiw-card-body--screenshot">
              <img src="/tasks.png" alt="Task board" class="hiw-screenshot" />
            </div>
          </div>

          <!-- Visual 1: Time tracking screenshot -->
          <div class="hiw-visual-card" :class="{ visible: currentStep === 1 }">
            <div class="hiw-card-header">
              <div style="display:flex;gap:5px;">
                <div class="app-preview-dot dot-red"></div>
                <div class="app-preview-dot dot-yellow"></div>
                <div class="app-preview-dot dot-green"></div>
              </div>
              <div class="hiw-card-title">{{ $t('hiw.visuals.trackerTitle') }}</div>
            </div>
            <div class="hiw-card-body hiw-card-body--screenshot">
              <img src="/timetracking.png" alt="Time tracking" class="hiw-screenshot" />
            </div>
          </div>

          <!-- Visual 2: Reports screenshot -->
          <div class="hiw-visual-card" :class="{ visible: currentStep === 2 }">
            <div class="hiw-card-header">
              <div style="display:flex;gap:5px;">
                <div class="app-preview-dot dot-red"></div>
                <div class="app-preview-dot dot-yellow"></div>
                <div class="app-preview-dot dot-green"></div>
              </div>
              <div class="hiw-card-title">{{ $t('hiw.visuals.reportsTitle') }}</div>
            </div>
            <div class="hiw-card-body hiw-card-body--screenshot">
              <img src="/report.png" alt="Time reports" class="hiw-screenshot" />
            </div>
          </div>

          <!-- Visual 3: Calendar screenshot -->
          <div class="hiw-visual-card" :class="{ visible: currentStep === 3 }">
            <div class="hiw-card-header">
              <div style="display:flex;gap:5px;">
                <div class="app-preview-dot dot-red"></div>
                <div class="app-preview-dot dot-yellow"></div>
                <div class="app-preview-dot dot-green"></div>
              </div>
              <div class="hiw-card-title">{{ $t('hiw.visuals.calendarTitle') }}</div>
            </div>
            <div class="hiw-card-body hiw-card-body--screenshot">
              <img src="/calendar.png" alt="Calendar view" class="hiw-screenshot" />
            </div>
          </div>

        </div>
      </div>
    </div>
  </section>
</template>

<script>
  export default {
    name: 'HowItWorksSection',
    data: () => ({
      currentStep: 0,
      stepInterval: null,
    }),
    mounted() {
      this.stepInterval = setInterval(() => {
        this.currentStep = (this.currentStep + 1) % 4
      }, 4000)
    },
    unmounted() {
      clearInterval(this.stepInterval)
    },
    computed: {
      titleHtml() {
        return this.$t('hiw.title').replace('\n', '<br/>')
      }
    }
  }
</script>

<style scoped>
.how-it-works { background: var(--bg); }

.hiw-grid {
  margin-top: 4rem;
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 5rem;
  align-items: center;
}

.hiw-steps { display: flex; flex-direction: column; gap: 0; }

.hiw-step {
  display: flex;
  gap: 1.25rem;
  padding: 1.5rem 0;
  border-bottom: 1px solid var(--border);
  cursor: pointer;
  transition: opacity var(--transition);
}

.hiw-step:last-child { border-bottom: none; }

.hiw-step:hover { opacity: 0.85; }

.step-num {
  flex-shrink: 0;
  width: 38px; height: 38px;
  border-radius: 50%;
  background: var(--border);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 0.85rem;
  font-weight: 700;
  color: var(--text-muted);
  transition: background var(--transition), color var(--transition);
}

.hiw-step.active .step-num {
  background: var(--primary);
  color: white;
}

.step-content h3 {
  font-size: 1rem;
  font-weight: 700;
  margin-bottom: 0.4rem;
  color: var(--text);
}

.step-content p {
  font-size: 0.875rem;
  color: var(--text-muted);
  line-height: 1.65;
}

.hiw-visual {
  position: relative;
  min-height: 360px;
}

.hiw-visual-card {
  background: white;
  border-radius: var(--radius-xl);
  border: 1px solid var(--border);
  box-shadow: var(--shadow-lg);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  position: absolute;
  inset: 0;
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.4s ease;
}

.hiw-visual-card.visible {
  opacity: 1;
  pointer-events: auto;
}

.hiw-card-header {
  background: #F0EDE9;
  padding: 0.75rem 1rem;
  display: flex;
  align-items: center;
  gap: 1rem;
}

.hiw-card-title {
  font-size: 0.8rem;
  font-weight: 600;
  color: var(--text-muted);
}

.hiw-card-body { padding: 1.25rem; flex: 1; }

.hiw-card-body--screenshot { padding: 0; overflow: hidden; }

.hiw-screenshot {
  width: 100%;
  display: block;
  object-fit: cover;
  object-position: top left;
}

/* Visual: dot colors (shared with HeroSection but scoped here for hiw visuals) */
.app-preview-dot {
  width: 11px; height: 11px;
  border-radius: 50%;
}

.dot-red { background: #FCA5A5; }
.dot-yellow { background: #FCD34D; }
.dot-green { background: #6EE7B7; }

@media (max-width: 900px) {
  .hiw-grid { grid-template-columns: 1fr; }
  .hiw-visual { order: -1; min-height: 300px; }
}

@media (max-width: 480px) {
  .hiw-visual { min-height: 240px; }
}
</style>
